<app-page>
    <app-page-header title="Seire">
        <a actions mat-stroked-button routerLink="/{{Routenames.seire}}/all">Alle checkpoints tonen</a>
        @if (Features.PlatformsSave | hasFeature) {
            <a actions mat-flat-button routerLink="/{{Routenames.platforms}}/new"><i class="fa-regular fa-plus me-1"></i> Platform toevoegen</a>
        }
    </app-page-header>

    <app-filter-tiles [(value)]="filter" [options]="filterOptions()"></app-filter-tiles>

    <app-section [title]="listTitle()" flush icon="grid-2" layout="stacked">
        <mat-checkbox (change)="groupByCompany.set($event.checked)" [checked]="groupByCompany()" actions>Groeperen per bedrijf</mat-checkbox>
        <mat-form-field actions class="search-field">
            <i class="fa-regular fa-magnifying-glass" matPrefix></i>
            <input (input)="query.set($any($event.target).value)"
                   [value]="query()"
                   aria-label="Zoek platform, bedrijf of URL"
                   matInput
                   placeholder="Zoek platform, bedrijf of URL"
                   type="search">
        </mat-form-field>

        <app-data-list columns="minmax(0,1.3fr) 150px 110px minmax(0,2fr) 16px" minWidth="860px">
            <div app-data-row chevron header>
                <span>Platform</span><span>Status</span><span>SSL verloopt</span><span>Omgevingen</span>
            </div>
            @for (group of groups(); track group.company) {
                @if (group.company !== null && group.rows.length) {
                    <app-day-group>
                        <span>{{ group.company || 'Geen bedrijf' }}</span>
                        <span class="group-count">{{ group.rows.length }} {{ group.rows.length === 1 ? 'platform' : 'platformen' }}</span>
                    </app-day-group>
                }
                @for (row of group.rows; track row.platform.id) {
                    <!-- Whole row links to the platform (stretched link), the URLs and company stay separate links -->
                    <div [tone]="row.status.key === 'error' ? 'error' : null" app-data-row chevron class="clickable position-relative">
                        <span class="ui-cell">
                            <a [routerLink]="['/', Routenames.seire, row.platform.id]" class="row-link stretched-link ui-ellipsis">{{ row.platform.name }}</a>
                            @if (group.company === null && row.platform.company) {
                                <a class="company-link ui-ellipsis position-relative z-2" routerLink="/{{Routenames.companies}}/{{row.platform.company.id}}">{{ row.platform.company.name }}</a>
                            }
                        </span>
                        <span class="status-cell">
                            <app-status-dot [tone]="row.status.tone">{{ row.status.label }}</app-status-dot>
                            <app-check-strip [items]="row.strip" class="position-relative z-2" size="sm"></app-check-strip>
                        </span>
                        <span [class.ui-error]="row.sslTone === 'error'" [class.ui-faint]="!row.sslDate" [class.ui-warn]="row.sslTone === 'warn'" class="ui-mono ssl">
                            {{ (row.sslDate | daysFromNow) ?? '—' }}
                        </span>
                        <span class="envs">
                            @for (env of row.envs; track env.env) {
                                <app-env-badge [env]="env.env">
                                    <a [href]="env.url" class="env-url ui-mono ui-ellipsis position-relative z-2" rel="noopener" target="_blank">{{ env.label }}</a>
                                </app-env-badge>
                            } @empty {
                                <span class="ui-faint">Geen URL's ingesteld</span>
                            }
                        </span>
                    </div>
                }
            }
            @if (!hasVisibleRows()) {
                <app-empty-state>{{ platforms() === null ? 'Aan het laden...' : 'Geen platformen gevonden.' }}</app-empty-state>
            }
        </app-data-list>

        <div class="legend">
            <app-env-badge env="P" showName></app-env-badge>
            <app-env-badge env="A" showName></app-env-badge>
            <app-env-badge env="T" showName></app-env-badge>
            <app-env-badge env="L" showName></app-env-badge>
            <span class="ms-auto">Staafjes onder de status: de levels {{ levelEnums.join(' · ') }}</span>
        </div>
    </app-section>

    @if (Features.Servers | hasFeature) {
        <app-section [meta]="serversMeta()" flush icon="server" layout="stacked" title="Servers">
            <app-data-list columns="minmax(0,1.6fr) 120px 120px 70px 80px 90px 150px" minWidth="1000px">
                <div app-data-row header>
                    <span>Server</span><span>Apache</span><span>PHP</span><span>OS</span><span>MySQL</span>
                    <span class="text-end">Updates</span><span class="text-end">Laatst bijgewerkt</span>
                </div>
                @for (row of serverRows(); track row.server.ip) {
                    <div app-data-row>
                        <span class="ui-cell">
                            <span [attr.title]="row.server.server_name" class="ui-strong ui-ellipsis">{{ row.server.server_name }}</span>
                            <span class="ui-mono ui-meta ui-ellipsis">{{ row.server.ip }}</span>
                        </span>
                        <span class="ui-cell">
                            <span class="ui-mono">{{ row.server.apache }}</span>
                            <span [class.ui-faint]="!row.moduleKnown" class="ui-meta">{{ row.server.apache_module }}</span>
                        </span>
                        <span class="ui-cell">
                            <span class="ui-mono">{{ row.server.php }}</span>
                            @if (row.phpDiffers) {
                                <span class="ui-meta ui-warn">Apache: {{ row.server.apache_php }}</span>
                            } @else {
                                <span class="ui-meta ui-faint">Apache gelijk</span>
                            }
                        </span>
                        <span [class.ui-warn]="row.osOutdated" class="ui-mono">{{ row.server.os }}</span>
                        <span class="ui-mono">{{ row.server.mysql }}</span>
                        <span class="text-end">
                            <app-status-badge [dot]="false" [tone]="row.manyUpdates ? 'warn' : 'neutral'" class="updates ui-mono">{{ row.server.updates }}</app-status-badge>
                        </span>
                        <span class="ui-cell text-end">
                            <span [class.stale]="row.stale">{{ row.server.updated_at | relativeDay }}</span>
                            <span class="ui-meta ui-faint">{{ row.server.updated_at | date:'EEE d MMM HH:mm' }}</span>
                        </span>
                    </div>
                } @empty {
                    <app-empty-state>Geen servers beschikbaar</app-empty-state>
                }
            </app-data-list>
        </app-section>
    }
</app-page>
